//Hooks
import { get } from 'svelte/store';
//Formating
import { format } from 'svelte-i18n';
//Helper
import { getTimeFormat } from 'src/helpers/date_helper';
import { isValidWebhookUrl } from '../validation/webhook_validation_helpers';
import { getComponentInputType } from '../core/webhook_components_helpers';
//Types
import type { WebHooksParams } from 'src/types/requests/settings/webhooks';
import type { LabelProps, StatsPropsTypes } from '@Shelly/shelly-design-system/index';
import { buildWebhookConditionLabels } from './webhook_conditions_view_helpers';

export const getWizardItems = (
	component: 'peripheral' | 'component' = null,
	hideTimePicker: boolean = false,
) => {
	const $format = get(format),
		wizardStep = [
			{
				title: $format('action_wizard_items_name', { default: 'Name' }),
				subtitle: $format('action_name', { default: 'Action name' }),
			},
			{
				title: $format('action_wizard_items_time', { default: 'Time' }),
				subtitle: $format('active_time', { default: 'Active time' }),
			},
			{
				title: $format('action_wizard_items_condition', {
					default: 'Condition',
				}),
			},
			{
				title: 'Urls',
				subtitle: $format('actions', { default: 'Actions' }),
			},
		];
	if (hideTimePicker) wizardStep.splice(1, 1);
	if (component) {
		wizardStep.unshift({
			title: $format(`type_${component}`, { default: component }),
			subtitle: $format(`choose_${component}`, {
				default: `Choose ${component}`,
			}),
		});
	}
	return wizardStep.map((item, i) => {
		return {
			...item,
			active: i === 0 ? true : false,
			stepNumber: i + 1,
			activeIndex: i,
		};
	});
};

export const buildWebhookLabels = (hook: WebHooksParams): LabelProps[] => {
	// hook.active_between can be an empty string in the case of Gen.2 Wall Display (SAWD-0A1XX10EU1 and SAWD-1A1XX10EU1)
	const time =
		hook.active_between && hook.active_between.length == 2
			? hook.active_between
			: ['00:00', '00:00'];
	const formattedTime = `${getTimeFormat.apply(
		time,
		time[0].split(':'),
	)} - ${getTimeFormat.apply(time, time[1].split(':'))}`;
	let labelList = [];
	const isVirtualWebHooks =
		/^(bthomesensor|bthomedevice|boolean|enum|text|button|number|presencezone|camerazone)\./i.test(
			hook.event,
		) && hook?.components?.length;
	let virtualWebHook: WebHooksParams;
	if (isVirtualWebHooks) {
		virtualWebHook = hook.components.find((i) => i.cid == hook.cid && hook.event.startsWith(i.type));
		if (virtualWebHook) {
			hook.icon = virtualWebHook.icon;
			hook.unit = virtualWebHook.unit;
			hook.enums = virtualWebHook.enums;
			hook.title = virtualWebHook.title;
		}
	}
	if (
		(hook.event.startsWith('input') &&
			!new RegExp(`(${getComponentInputType(hook)})`).test(hook.event)) ||
		(/^(input|voltmeter)/.test(hook.event) &&
			((/(xtotal|xpercent|xvoltage)/.test(hook.condition) && !hook.xunit) ||
				(/xfreq/.test(hook.condition) && !hook.freqXUnit))) ||
		!isValidWebhookUrl(hook.urls)
	) {
		labelList.push(
			//@ts-ignore
			{
				value: '',
				icon: 'fas fa-triangle-exclamation',
				iconColor: hook.enable ? '--color-warning' : '--color-base',
			},
		);
	}
	labelList = buildWebhookConditionLabels(hook, labelList);
	if (hook.active_between?.length) {
		labelList.push({ value: formattedTime, icon: 'fas fa-clock' });
	}
	labelList.push({ value: hook.urls.length, icon: 'fas fa-brackets' });
	return labelList;
};

export const buildWebhookStatusBarStats = (hook: WebHooksParams): StatsPropsTypes[] => {
	const $format = get(format);
	if (hook.inputType === 'count|freq') {
		return [
			{
				icon: hook.icon || '',
				iconColor: 'secondary',
				text: $format('settings_title_plus_uni_counter', {
					default: 'Plus Uni Counter',
				}),
			},
		];
	}
	if (hook.event?.startsWith('lnm.')) {
		return [
			{
				icon: 'fas fa-network-wired',
				iconColor: 'danger',
				text: $format('lnm_instance_title', {
					values: { id: hook.cid },
					default: 'LNM instance ({id})',
				}),
			},
		];
	}
	if (hook.cid < 100) {
		return [
			{
				icon: 'far fa-microchip',
				iconColor: 'primary',
				text: hook?.deviceName || `${hook.type}:${hook.cid}`,
			},
		];
	}
	if (hook.cid < 200) {
		return [
			{
				icon: 'far fa-puzzle-piece',
				iconColor: 'warning',
				text: $format('label_peripheral', {
					default: 'Peripheral component',
				}),
			},
		];
	}
	return [
		{
			icon: 'far fa-cube',
			iconColor: 'success',
			text: $format('label_virtual_component', {
				default: 'Virtual component',
			}),
		},
	];
};

export const DEVICES_WITHOUT_ACTIVE_TIME_STEP: string[] = [
	'SNSN-0031Z', //Shelly Plus Smoke
	'S4SN-1031Z', //Heiman Smoke EU
	'S4SN-0031Z', //Heiman Smoke US
	'SMSN-0031ZL', //Smoke LTE
];
